import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Paper,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Tooltip,
    IconButton,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import {
    Upload as UploadIcon,
    Link as LinkIcon,
    Router as RouterIcon,
    VideoFile as VideoFileIcon,
    InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';

// Import the new separate components
import ManualUrlComponent from './ManualUrlComponent.jsx';
import UploadFileComponent from './UploadFileComponent.jsx';
import TransportLayerComponent from './TransportLayerComponent.jsx';

const parseTransportLink = (value = '') => {
    const normalized = String(value || '');
    const match = normalized.match(/^transport@(\d+):(.*)$/);
    if (!match) {
        return null;
    }

    return {
        deviceId: match[1],
        streamUrl: match[2] || '',
    };
};

const LinkSettings = ({
    initialLink = '',
    initialUploadedLink = '0',
    initialFullframeProcessing = false,
    initialNvrVendor = '',
    availableHorns = [],
    initialHornId = -1,
    disabled = false,
    item = {},
    canUpload = false,
    uploadUrl = '',
    uploadFilesUrl = '',
    maxUploadSize = '5 GB',
    nvrVendors = [],
    transportDevices = [],
    isOwner = false,
    isAdmin = false,
    errors = {}
}) => {
    const [mode, setMode] = useState('manual'); // 'manual', 'transport', 'upload'
    const [link, setLink] = useState(initialLink);
    const [uploadedLink, setUploadedLink] = useState(initialUploadedLink);
    const [fullframeProcessing, setFullframeProcessing] = useState(initialFullframeProcessing);
    const [selectedTransportDevice, setSelectedTransportDevice] = useState('');
    const [selectedTransportStream, setSelectedTransportStream] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadedSize, setUploadedSize] = useState(null);
    const [uploadFilesLoading, setUploadFilesLoading] = useState(false);
    const [uploadFilesError, setUploadFilesError] = useState('');
    const [uploadFilesLoaded, setUploadFilesLoaded] = useState(false);
    const uploadFilesRequest = useRef(null);
    const uploadChanges = useRef(0);
    const [selectedUploadedFile, setSelectedUploadedFile] = useState('');
    const [manualLink, setManualLink] = useState(initialLink); // Preserve manual link
    const [nvrVendor, setNvrVendor] = useState(initialNvrVendor);
    const [selectedHornId, setSelectedHornId] = useState(String(initialHornId ?? -1));
    const [sourceInitialized, setSourceInitialized] = useState(false);

    // Memoize transport devices to prevent recreating the array on every render
    const connectedTransportDevices = useMemo(() =>
        transportDevices.filter(td =>
            td.transport_device_id === item.device_id
        ), [transportDevices, item.device_id]
    );

    const visibleHornOptions = useMemo(() => {
        const activeDeviceIds = new Set([Number(item.device_id || 0)]);
        if (mode === 'transport' && selectedTransportDevice) {
            activeDeviceIds.add(Number(selectedTransportDevice));
        }

        return (Array.isArray(availableHorns) ? availableHorns : []).filter((horn) => (
            activeDeviceIds.has(Number(horn?.device_id || 0))
        ));
    }, [availableHorns, item.device_id, mode, selectedTransportDevice]);

    // Initialize uploaded files and mode based on current state
    useEffect(() => {
        // A readonly form displays the saved source without resolving alternative catalogs.
        if (disabled) {
            const transport = parseTransportLink(initialLink);
            if (transport || String(initialUploadedLink) === '2') {
                setMode('transport');
                setSelectedTransportDevice(transport?.deviceId ?? String(connectedTransportDevices[0]?.id ?? ''));
                setSelectedTransportStream(transport?.streamUrl ?? initialLink);
            } else if (String(initialUploadedLink) === '1') {
                setMode('upload');
                setSelectedUploadedFile(initialLink);
            }
            setSourceInitialized(true);
            return;
        }
        const parsedTransportLink = parseTransportLink(initialLink);
        if (parsedTransportLink) {
            let resolvedSelection = false;
            const deviceById = connectedTransportDevices.find((td) => td.id.toString() === parsedTransportLink.deviceId);

            if (deviceById) {
                const streams = getTransportStreams(deviceById);
                const matchingStream = streams.find((stream) => stream.url === parsedTransportLink.streamUrl);
                setMode('transport');
                setUploadedLink('2');
                setSelectedTransportDevice(deviceById.id.toString());
                setSelectedTransportStream(matchingStream ? matchingStream.url : parsedTransportLink.streamUrl);
                resolvedSelection = true;
            }

            if (!resolvedSelection) {
                for (const device of connectedTransportDevices) {
                    const streams = getTransportStreams(device);
                    const foundStream = streams.find((stream) => stream.url === parsedTransportLink.streamUrl);
                    if (foundStream) {
                        setMode('transport');
                        setUploadedLink('2');
                        setSelectedTransportDevice(device.id.toString());
                        setSelectedTransportStream(foundStream.url);
                        resolvedSelection = true;
                        break;
                    }
                }
            }

            if (!resolvedSelection) {
                setMode('transport');
                setUploadedLink('2');
                setSelectedTransportDevice(parsedTransportLink.deviceId);
                setSelectedTransportStream(parsedTransportLink.streamUrl);
            }
        } else if (initialUploadedLink === '1' || initialUploadedLink === 1) {
            setMode('upload');
            if (initialLink) {
                setSelectedUploadedFile(initialLink);
            }
        } else {
            setMode('manual');
        }
        setSourceInitialized(true);
    }, []); // Empty dependency array - only run once on mount

    /** Cancel an unfinished manual scan when navigating away from the form. */
    useEffect(() => () => uploadFilesRequest.current?.abort(), []);

    useEffect(() => {
        // Update hidden inputs when state changes
        const linkInput = document.getElementById('input-link');
        const uploadedLinkInput = document.getElementById('input-uploadedLink');
        const fullframeInput = document.querySelector('input[name="fullframe_processing"]');
        const hornIdInput = document.getElementById('horn_id');

        if (linkInput) {
            linkInput.value = link;
            linkInput.dispatchEvent(new Event('input', { bubbles: true }));
            linkInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (uploadedLinkInput) {
            uploadedLinkInput.value = uploadedLink;
            uploadedLinkInput.dispatchEvent(new Event('input', { bubbles: true }));
            uploadedLinkInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (fullframeInput) {
            fullframeInput.value = fullframeProcessing ? 'on' : '';
            fullframeInput.dispatchEvent(new Event('input', { bubbles: true }));
            fullframeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (hornIdInput) {
            hornIdInput.value = selectedHornId;
            hornIdInput.dispatchEvent(new Event('input', { bubbles: true }));
            hornIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        window.dispatchEvent(new CustomEvent('item:hornSelectionChanged', {
            detail: {
                hornId: selectedHornId,
                audioMessageEnabled: selectedHornId !== '-1' && mode !== 'upload',
            },
        }));

        // Trigger form validation after updating hidden inputs
        const validationTimer = setTimeout(() => {
            const form = document.getElementById('editForm');
            if (form) {
                // Check if form is valid and enable/disable submit button accordingly
                const submitBtn = document.getElementById('submit-btn');
                if (submitBtn) {
                    // Re-enable the submit button if we have a valid link
                    const hasValidLink = link && link.trim() !== '';
                    if (hasValidLink) {
                        submitBtn.disabled = false;
                    }
                }
            }
        }, 100); // Small delay to ensure DOM updates are complete
        return () => clearTimeout(validationTimer);
    }, [fullframeProcessing, link, mode, selectedHornId, uploadedLink]);

    useEffect(() => {
        if (disabled || !sourceInitialized) {
            return;
        }

        const selectedHornStillVisible = visibleHornOptions.some((horn) => String(horn.id) === String(selectedHornId));
        if (selectedHornId === '-1' || selectedHornStillVisible) {
            return;
        }

        setSelectedHornId('-1');
    }, [disabled, selectedHornId, sourceInitialized, visibleHornOptions]);

    const getTransportStreams = (device) => {
        if (!device || !device.transport_layer_config) return [];

        try {
            const config = typeof device.transport_layer_config === 'string'
                ? JSON.parse(device.transport_layer_config)
                : device.transport_layer_config;
            return Array.isArray(config) ? config : [];
        } catch (e) {
            return [];
        }
    };

    const handleModeChange = (newMode) => {
        console.log('Changing mode from', mode, 'to', newMode);

        // Save current manual link if we're leaving manual mode
        if (mode === 'manual' && newMode !== 'manual') {
            setManualLink(link);
        }

        setMode(newMode);

        // Reset state based on mode
        switch (newMode) {
            case 'manual':
                setUploadedLink('0');
                setSelectedTransportDevice('');
                setSelectedTransportStream('');
                setSelectedUploadedFile('');
                // Restore the saved manual link instead of clearing it
                setLink(manualLink || link);
                break;
            case 'transport':
                setUploadedLink('2');
                setSelectedUploadedFile('');
                setLink(''); // Clear until stream is selected
                break;
            case 'upload':
                setUploadedLink('1');
                setSelectedTransportDevice('');
                setSelectedTransportStream('');
                setNvrVendor('');
                // If we have uploaded files, select the first one
                if (uploadedFiles.length > 0 && !selectedUploadedFile) {
                    handleUploadedFileSelect(uploadedFiles[0].url);
                }
                break;
        }
    };

    // Manual URL handlers
    const handleManualLinkChange = (newLink) => {
        setLink(newLink);
        // Also update the manual link state when user types in manual mode
        if (mode === 'manual') {
            setManualLink(newLink);
        }
    };

    // Transport layer handlers
    const handleTransportDeviceChange = (deviceId) => {
        setSelectedTransportDevice(deviceId);
        setSelectedTransportStream('');
        if (deviceId) {
            setLink(''); // Clear link until stream is selected
        }
    };

    const handleTransportStreamChange = (streamUrl) => {
        setSelectedTransportStream(streamUrl);
        console.log('Selected transport stream:', { selectedTransportDevice, streamUrl });
        let fullStreamUrl = streamUrl;
        if (selectedTransportDevice) {
            fullStreamUrl = `transport@${selectedTransportDevice}:${streamUrl}`;
        }

        if (!isRtspUrl(streamUrl)) {
            setNvrVendor('');
        }

        setLink(fullStreamUrl);
    };

    /** Fetch inventory on a button press without changing the saved source selection. */
    const loadUploadFiles = async () => {
        if (disabled || !canUpload || !uploadFilesUrl || uploadFilesRequest.current) {
            return;
        }

        const controller = new AbortController();
        const revision = uploadChanges.current;
        uploadFilesRequest.current = controller;
        setUploadFilesLoading(true);
        setUploadFilesError('');

        try {
            const response = await fetch(uploadFilesUrl, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                cache: 'no-store',
                signal: controller.signal,
            });
            if (!response.ok) {
                throw new Error('Could not load upload files. Please try again.');
            }
            const data = await response.json();
            if (!Array.isArray(data.files) || typeof data.uploaded_size !== 'string') {
                throw new Error('Could not load upload files. Please try again.');
            }
            if (controller.signal.aborted) {
                return;
            }

            const availableFiles = data.files.map(file => ({
                ...file,
                url: file.name,
                isExisting: true,
            }));
            setUploadedFiles(previous => [
                ...availableFiles,
                ...previous.filter(file => !file.isExisting && !availableFiles.some(available => available.url === file.url)),
            ]);
            if (revision === uploadChanges.current) {
                setUploadedSize(data.uploaded_size);
            }
            setUploadFilesLoaded(true);
        } catch (error) {
            if (!controller.signal.aborted) {
                setUploadFilesError('Could not load upload files. Please try again.');
            }
        } finally {
            uploadFilesRequest.current = null;
            if (!controller.signal.aborted) {
                setUploadFilesLoading(false);
            }
        }
    };

    // Upload file handlers
    const handleFileUpload = (files, isRealUpload = false, currentSize = null) => {
        uploadChanges.current += 1;
        setUploadedSize(currentSize);
        console.log('Files uploaded:', files, 'isRealUpload:', isRealUpload);

        if (isRealUpload) {
            // This is a real upload from Resumable.js, add files directly
            setUploadedFiles(prev => [...prev, ...files]);

            // Auto-select the first uploaded file
            if (files.length > 0) {
                handleUploadedFileSelect(files[0].url);
            }
        } else {
            // This is a simulated upload (shouldn't happen with real implementation)
            const newFiles = files.map(file => ({
                name: file.name,
                size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                url: file.name
            }));

            setUploadedFiles(prev => [...prev, ...newFiles]);

            // Auto-select the first uploaded file
            if (newFiles.length > 0) {
                handleUploadedFileSelect(newFiles[0].url);
            }
        }
    };

    const handleUploadedFileSelect = (fileUrl) => {
        setSelectedUploadedFile(fileUrl);
        setLink(fileUrl);
    };

    const handleUploadedFileDelete = (fileUrl) => {
        uploadChanges.current += 1;
        setUploadedSize(null);
        setUploadedFiles(prev => prev.filter(file => file.url !== fileUrl));
        if (selectedUploadedFile === fileUrl) {
            setSelectedUploadedFile('');
            setLink('');
        }
    };

    const handleFullframeProcessingChange = (checked) => {
        setFullframeProcessing(checked);
    };

    const isRtspUrl = (value = '') => {
        const normalizedValue = String(value).trim().toLowerCase();

        return normalizedValue.startsWith('rtsp://') || normalizedValue.startsWith('rtsps://');
    };

    const shouldShowNvrSettings = mode !== 'upload' && (mode !== 'transport' || isRtspUrl(selectedTransportStream));

    const nvrTooltipMessage = 'Vendor selection is the only NVR-specific setting right now. Leave it as Not NVR when the source has no recording-download mapping.';

    const renderModeSelector = () => {
        if (disabled) return null;

        // Check if each mode is available
        const hasTransportDevices = connectedTransportDevices.length > 0;
        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Select Video Source Type
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button
                        variant={mode === 'manual' ? 'contained' : 'outlined'}
                        onClick={() => handleModeChange('manual')}
                        startIcon={<LinkIcon />}
                        sx={{ flex: 1, minHeight: 48 }}
                    >
                        Manual URL
                    </Button>

                    <Button
                        variant={mode === 'transport' ? 'contained' : 'outlined'}
                        onClick={() => handleModeChange('transport')}
                        startIcon={<RouterIcon />}
                        disabled={!hasTransportDevices}
                        sx={{ flex: 1, minHeight: 48 }}
                    >
                        Transport Layer
                        {!hasTransportDevices && (
                            <Chip size="small" label="N/A" sx={{ ml: 1 }} />
                        )}
                    </Button>

                    <Button
                        variant={mode === 'upload' ? 'contained' : 'outlined'}
                        onClick={() => handleModeChange('upload')}
                        startIcon={<UploadIcon />}
                        disabled={!canUpload}
                        sx={{ flex: 1, minHeight: 48 }}
                    >
                        Upload File
                        {!canUpload && (
                            <Chip size="small" label="N/A" sx={{ ml: 1 }} />
                        )}
                    </Button>
                </Box>

                {/* Info about disabled options */}
                {(!hasTransportDevices || !canUpload) && (
                    <Box sx={{ mt: 1 }}>
                        {!hasTransportDevices && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                • Transport Layer: No connected transport devices found
                            </Typography>
                        )}
                        {!canUpload && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                • Upload File: Only available for system devices in draft status
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const renderCurrentSelection = () => {
        if (!link && mode !== 'upload') return null;

        return (
            <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    <strong>Selected source:</strong> {
                        mode === 'manual' ? `Manual URL: ${link}` :
                        mode === 'transport' ? `Transport: ${selectedTransportStream || link}` :
                        mode === 'upload' ? `Uploaded file: ${selectedUploadedFile || link}` :
                        link
                    }
                </Typography>
            </Alert>
        );
    };

    const renderNvrSettings = () => {
        if (mode === 'upload') {
            return null;
        }

        return (
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        NVR Recording
                        <Tooltip title={nvrTooltipMessage} placement="top">
                            <IconButton size="small" sx={{ p: 0.25 }} aria-label="NVR recording information">
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Typography>

                    <FormControl fullWidth size="small" error={Boolean(errors.nvr_vendor)}>
                        <InputLabel>NVR Vendor</InputLabel>
                        <Select
                            value={nvrVendor}
                            label="NVR Vendor"
                            onChange={(event) => setNvrVendor(event.target.value)}
                            disabled={disabled}
                        >
                            <MenuItem value="">
                                <em>Not NVR</em>
                            </MenuItem>
                            {nvrVendors.map((vendor) => (
                                <MenuItem key={vendor.value} value={vendor.value}>
                                    {vendor.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </CardContent>
            </Card>
        );
    };

    const renderHornSettings = () => {
        if (!Array.isArray(availableHorns) || availableHorns.length === 0) {
            return null;
        }

        return (
            <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                        Horn Routing
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select one horn from the main device or from the currently selected transport device.
                    </Typography>
                    <FormControl fullWidth size="small" error={Boolean(errors.horn_id)} sx={{ mb: selectedHornId !== '-1' ? 2 : 0 }}>
                        <InputLabel>Horn</InputLabel>
                        <Select
                            value={selectedHornId}
                            label="Horn"
                            onChange={(event) => setSelectedHornId(event.target.value)}
                            disabled={disabled}
                        >
                            <MenuItem value="-1">
                                <em>No horn</em>
                            </MenuItem>
                            {visibleHornOptions.map((horn) => (
                                <MenuItem key={horn.id} value={String(horn.id)}>
                                    {horn.name}{horn.device_name ? ` — ${horn.device_name}` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </CardContent>
            </Card>
        );
    };

    const renderSourceConfiguration = () => {
        if (mode === 'manual') {
            return (
                <ManualUrlComponent
                    link={link}
                    onLinkChange={handleManualLinkChange}
                    disabled={disabled}
                    errors={errors}
                />
            );
        }

        if (mode === 'transport') {
            return (
                <TransportLayerComponent
                    transportDevices={connectedTransportDevices}
                    selectedTransportDevice={selectedTransportDevice}
                    selectedTransportStream={selectedTransportStream}
                    onTransportDeviceChange={handleTransportDeviceChange}
                    onTransportStreamChange={handleTransportStreamChange}
                    isOwner={isOwner}
                    isAdmin={isAdmin}
                    disabled={disabled}
                />
            );
        }

        if (mode === 'upload') {
            return (
                <Box>
                    <UploadFileComponent
                        uploadedFiles={uploadedFiles}
                        selectedUploadedFile={selectedUploadedFile}
                        onFileUpload={handleFileUpload}
                        onFileSelect={handleUploadedFileSelect}
                        onFileDelete={handleUploadedFileDelete}
                        disabled={disabled || !canUpload}
                        item={item}
                        uploadUrl={uploadUrl}
                        maxUploadSize={maxUploadSize}
                        uploadedSize={uploadedSize}
                        onLoadAvailableFiles={canUpload && uploadFilesUrl ? loadUploadFiles : null}
                        filesLoading={uploadFilesLoading}
                        filesLoaded={uploadFilesLoaded}
                        filesError={uploadFilesError}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={fullframeProcessing}
                                onChange={(event) => handleFullframeProcessingChange(event.target.checked)}
                                disabled={disabled}
                                name="fullframe_processing"
                            />
                        }
                        label="Fullframe processing"
                    />
                </Box>
            );
        }

        return null;
    };

    return (
        <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <VideoFileIcon sx={{ mr: 1 }} />
                Video Source Configuration
            </Typography>

            {renderModeSelector()}
            {renderCurrentSelection()}

            {mode === 'upload' ? (
                renderSourceConfiguration()
            ) : shouldShowNvrSettings ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'minmax(0, 3fr) minmax(0, 1fr)'
                        },
                        gap: 2,
                        alignItems: 'start'
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        {renderSourceConfiguration()}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        {renderNvrSettings()}
                    </Box>
                </Box>
            ) : (
                renderSourceConfiguration()
            )}

            {renderHornSettings()}

            {/* Hidden inputs for form submission */}
            <input
                type="hidden"
                name="link"
                id="input-link"
                value={link}
            />
            <input
                type="hidden"
                name="uploaded_link"
                id="input-uploadedLink"
                value={uploadedLink}
            />
            <input
                type="hidden"
                name="fullframe_processing"
                value={fullframeProcessing ? 'on' : ''}
            />
            <input
                type="hidden"
                name="nvr_vendor"
                value={nvrVendor}
            />
            <input
                type="hidden"
                name="horn_id"
                id="horn_id"
                value={selectedHornId}
            />
        </Paper>
    );
};

export default LinkSettings;
